import { useState, useEffect } from "react";
import "./JobSpecPanel.css";
import {
  getJobDescription,
  getJobDescriptionLogs,
  getSkillSetMatrix,
  getSkillSetMatrixNonTech,
} from "./api";
import { parseJobDescriptionSections } from "./jobDescriptionSections";

// Verticals where the real app hides the Skill Set Matrix behind a button +
// modal (skills-matrix-info.component.ts) instead of showing it inline
// (skills-matrix.component.ts) - ported from job-specification.component.html's
// `@if (db.stackModel.verticalStackId === 9 || ... === 10 || ... === 11 || ... === 3)`.
export const SKILL_MATRIX_MODAL_VERTICALS = [9, 10, 11, 3];

// Static Rating Scale legend (app-rating-scales) - the real component reads
// this from SkillMatrixService.dataArr2, a hardcoded array, not fetched data.
// Text (including the apparent typos "Famianity"/"buting") transcribed
// verbatim from the live staging app, not corrected - fidelity to the real
// content is the point here, not our own copy-editing.
const RATING_SCALE = [
  { rating: 1, color: "#D64550", description: "Basic Famianity-Independent basic usage can work with existing code" },
  { rating: 2, color: "#E08A2E", description: "Working practical knowledge, independent basic usage, can work with existing code" },
  { rating: 3, color: "#C9A227", description: "Competent general usage, can develop solutions from scratch, new functionality" },
  { rating: 4, color: "#4C9A5B", description: "Proficient advanced usage, optimization, architectural design, helps juniors buting" },
  { rating: 5, color: "#1B6E3C", description: "Highly expert, comprehensive knowledge of best practice, advanced coaching" },
];

function SectionList({ sections }) {
  if (!sections || sections.length === 0) {
    return <div className="jsp-empty">No Data found</div>;
  }
  return (
    <div>
      {sections.map((s, i) => (
        <div key={i}>
          {s.title && <div className="jsp-section-title">{s.title}</div>}
          <ul className="jsp-list">
            {s.points.map((p, j) => (
              <li key={j}>{p}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// Real SQL field -> display label for the Skill Set Matrix info card.
// Two DIFFERENT field sets confirmed via the two real templates - not the
// same content in two containers:
//  - skills-matrix.component.html (inline case): only 5 of these fields.
//  - skills-matrix-info.component.html (modal case, verticals 9/10/11/3):
//    the full 9-field set, via skillMatrixFields.
const SKILL_MATRIX_FIELDS_INLINE = [
  { label: "Client", key: "client" },
  { label: "Role", key: "role" },
  { label: "Direct Line Manager", key: "direct_line_manager" },
  { label: "Direct Reports", key: "direct_report" },
  { label: "Date Completed", key: "date_completed" },
];
const SKILL_MATRIX_FIELDS_MODAL = [
  { label: "Client", key: "client" },
  { label: "Role", key: "role" },
  { label: "Level & Value", key: "level_and_value" },
  { label: "Typical Work Experience", key: "work_experience" },
  { label: "Relevant Stacks", key: "relevant_stacks" },
  { label: "Best Job Sub Family", key: "best_job_sub_family" },
  { label: "Direct Line Manager", key: "direct_line_manager" },
  { label: "Direct Reports", key: "direct_report" },
  { label: "Date Completed", key: "date_completed" },
];

// "Non-Tech Skills" legend (dataArr3 in skill-matrix.service.ts) - only
// shown in the MODAL case, alongside (not instead of) the 1-5 Rating Scale
// above it. Exact hex colors confirmed from the real service, not guessed.
const NON_TECH_SKILLS_SCALE = [
  { rating: 1, color: "#682dcc", description: "Entry" },
  { rating: 2, color: "#9c064d", description: "Experienced" },
  { rating: 3, color: "#00546b", description: "Expert" },
];

// Ported from rating-scales.component.html exactly: the "Rating Scales"
// heading sits ABOVE the colored box (`<div class="bold">Rating
// Scales</div>` then, separately, `<div class="skill-section-table"
// [style.backgroundColor]>`), not inside it - was wrongly nested inside the
// box before this fix. Items are a vertical list (col-12 per item in the
// real template).
function RatingScaleLegend() {
  return (
    <div className="jsp-legend-wrap">
      <div className="jsp-subheading">Rating Scales</div>
      <div className="jsp-legend-box jsp-rating-scales">
        {RATING_SCALE.map((r) => (
          <div className="jsp-rating-row" key={r.rating}>
            <span className="jsp-rating-badge" style={{ background: r.color }}>{r.rating}</span>
            <span className="jsp-rating-desc">{r.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Ported from skills-matrix-info.component.html's Non-Tech Skills block:
// heading above the box (same pattern as Rating Scales), items are a
// HORIZONTAL row (`<div class="row">...<div class="col">`, plain Bootstrap
// .col = equal-width side by side, not a stacked list - that was the bug).
function NonTechSkillsLegend() {
  return (
    <div className="jsp-legend-wrap">
      <div className="jsp-subheading">Non-Tech Skills</div>
      <div className="jsp-legend-box jsp-nontech-row">
        {NON_TECH_SKILLS_SCALE.map((r) => (
          <div className="jsp-nontech-item" key={r.rating}>
            <span className="jsp-rating-badge" style={{ background: r.color }}>{r.rating}</span>
            <span className="jsp-rating-desc">{r.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SkillMatrixFieldsCard({ fields, info, roleName }) {
  return (
    <div className="jsp-skillmatrix-info">
      {fields.map(({ label, key }) => (
        <div className="jsp-skill-row" key={key}>
          <span className="jsp-skill-label">{label}:</span>
          <span className="jsp-skill-value">
            {key === "role" ? roleName || info?.role || "" : info?.[key] || ""}
          </span>
        </div>
      ))}
    </div>
  );
}

// Ported from splitSkillsString() - splits a newline-delimited skills blob
// (must_have_skills / good_to_have_skills) into individual rows, dropping
// blank lines, matching the real `@if (skill.trim() !== '')` guard.
function splitSkillsString(value) {
  if (!value) return [];
  return value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
}

// Inline case (skills-matrix.component.html) - 5-field info card + Rating
// Scale + the "Rating" table (from get_skill_set_matrix_non_tech.sql).
function InlineSkillMatrix({ info, nonTechData, roleName }) {
  return (
    <div className="jsp-skillmatrix">
      {/* Ported from skills-matrix.component.html's `<h5 class="bold">Skill
          Set Matrix</h5>` - was missing entirely before this fix. */}
      <h3 className="jsp-subheading jsp-matrix-heading">Skill Set Matrix</h3>
      <SkillMatrixFieldsCard fields={SKILL_MATRIX_FIELDS_INLINE} info={info} roleName={roleName} />
      <RatingScaleLegend />

      <div className="jsp-subheading jsp-rating-heading">Rating</div>
      <div className="jsp-rating-table-wrap">
        <table className="jsp-rating-table">
          <thead>
            <tr>
              <th>Hard Skills</th>
              <th>
                Experience Level
                <div className="jsp-th-sub">(entry/mid/senior)</div>
              </th>
              <th>Skills Theme</th>
              <th>Rating (importance)</th>
              <th>Supporting Details</th>
            </tr>
          </thead>
          <tbody>
            {!nonTechData || nonTechData.length === 0 ? (
              <tr>
                <td colSpan={5} className="jsp-empty-cell">No Data found</td>
              </tr>
            ) : (
              nonTechData.map((s, i) => (
                <tr key={i}>
                  <td>{s.skills}</td>
                  <td className="jsp-center">{s.level}</td>
                  <td>{s.skills_theme}</td>
                  {/* Genuinely empty/unbound in the real app too - not a gap to backfill. */}
                  <td></td>
                  <td></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Static placeholder rows for "Background And Experience" (dataArr6 in
// skill-matrix.service.ts) - genuinely empty/hardcoded in the real app, not
// wired to any per-role data at all. Ported as-is, headers included
// verbatim (the "Candiate" typo is real, present in the live source).
const BACKGROUND_EXPERIENCE_ROWS = new Array(5).fill(null);
const BACKGROUND_EXPERIENCE_HEADERS = [
  "Background And Experience", "YRS", "Details",
  "Candiate1: 'ABC'", "Candiate2: 'DEF'", "Candiate3: 'DEF'",
];

// Column headers for "Must Haves's" / "Nice to Haves's" - only the first
// (real skill) column has data; the rest are genuinely empty/unbound
// scaffolding in the real app (skills-matrix-info.component.html renders
// literal `&nbsp;` for every one of them). Must Haves's has one more column
// ("Skills Theme") than Nice to Haves's - a real asymmetry in the source,
// not a typo to "fix".
const MUST_HAVE_EXTRA_COLUMNS = [
  "Skills Theme", "Experince Using the Skills(YRS): 'DEF'", "Skills level Rating(1 to 5)",
  "Applicable Test (select from dropDown)", "Test Scroll Requirements (/10)", "Commentary",
  "Meets Requirements?", "Meets Requirements?", "Meets Requirements?",
];
const NICE_TO_HAVE_EXTRA_COLUMNS = [
  "Experince Using the Skills(YRS): 'DEF'", "Skills level Rating(1 to 5)",
  "Applicable Test (select from dropDown)", "Test Scroll Requirements (/10)", "Commentary",
  "Meets Requirements?", "Meets Requirements?", "Meets Requirements?",
];

function SkillsBlobTable({ heading, skills, extraColumns }) {
  return (
    <div className="jsp-blob-table-wrap">
      <div className="jsp-subheading">{heading}</div>
      <table className="jsp-blob-table">
        <thead>
          <tr>
            <th>{heading}</th>
            {extraColumns.map((c, i) => (
              <th key={i}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {skills.length === 0 ? (
            <tr>
              <td colSpan={extraColumns.length + 1} className="jsp-empty-cell">No Data found</td>
            </tr>
          ) : (
            skills.map((skill, i) => (
              <tr key={i}>
                <td>{skill}</td>
                {extraColumns.map((_, j) => (
                  <td key={j}>&nbsp;</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Modal case (skills-matrix-info.component.html, verticals 9/10/11/3) -
// full 9-field info card + Rating Scale + Non-Tech Skills legend +
// Background And Experience (static) + Must Haves's / Nice to Haves's
// (real data from get_skill_set_matrix.sql's must_have_skills/
// good_to_have_skills columns).
function ModalSkillMatrix({ info, roleName }) {
  const mustHaves = splitSkillsString(info?.must_have_skills);
  const niceToHaves = splitSkillsString(info?.good_to_have_skills);

  return (
    <div className="jsp-skillmatrix jsp-skillmatrix-modal">
      <div className="jsp-modal-two-col">
        <div>
          {/* Ported from skills-matrix-info.component.html's
              `<h6 class="bold pt-3">Skills Matrix</h6>` - was missing
              entirely before this fix. */}
          <h3 className="jsp-subheading jsp-matrix-heading">Skills Matrix</h3>
          <SkillMatrixFieldsCard fields={SKILL_MATRIX_FIELDS_MODAL} info={info} roleName={roleName} />
        </div>
        <div>
          <RatingScaleLegend />
          <NonTechSkillsLegend />
        </div>
      </div>

      <div className="jsp-blob-table-wrap">
        <div className="jsp-subheading">Background And Experience</div>
        <table className="jsp-blob-table">
          <thead>
            <tr>
              {BACKGROUND_EXPERIENCE_HEADERS.map((h, i) => (
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BACKGROUND_EXPERIENCE_ROWS.map((_, i) => (
              <tr key={i}>
                {BACKGROUND_EXPERIENCE_HEADERS.map((_, j) => (
                  <td key={j}>&nbsp;</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SkillsBlobTable heading="Must Haves's" skills={mustHaves} extraColumns={MUST_HAVE_EXTRA_COLUMNS} />
      <SkillsBlobTable heading="Nice to Haves's" skills={niceToHaves} extraColumns={NICE_TO_HAVE_EXTRA_COLUMNS} />
    </div>
  );
}

// --- Change History (ported from job-specification.component.ts's ---
// --- TRACKED_FIELDS / LONG_TEXT_FIELDS / initialFields() / diffLogs()) ---
// Field keys are our own snake_case job_description_log columns (see
// get_job_description_logs.sql), not the old .NET camelCase equivalents.
const TRACKED_FIELDS = [
  { key: "minerva_job_title", label: "Minerva Job Title" },
  { key: "minerva_job_title_alt", label: "Alt Job Title" },
  { key: "vertical_stack", label: "Vertical Stack" },
  { key: "sub_stack", label: "Sub Stack" },
  { key: "minerva_level", label: "Level" },
  { key: "minerva_job_code", label: "Job Code" },
  { key: "source_job_code", label: "Source Job Code" },
  { key: "source_job_title", label: "Source Job Title" },
  { key: "core_or_non_core", label: "Core / Non-Core" },
  { key: "org_size", label: "Org Size" },
  { key: "experience", label: "Experience" },
  { key: "reporting_to", label: "Reporting To" },
  { key: "qualifications", label: "Qualifications" },
  { key: "certifications", label: "Certifications" },
  { key: "job_summary", label: "Job Summary" },
  { key: "key_responsibilities", label: "Key Responsibilities" },
  { key: "key_skills_and_competencies", label: "Key Skills & Competencies" },
  { key: "kpi", label: "KPI" },
  { key: "ai_experience", label: "AI Experience" },
  { key: "ai_job_summary", label: "AI Job Summary" },
  { key: "ai_qualifications", label: "AI Qualifications" },
  { key: "ai_key_responsibilities", label: "AI Key Responsibilities" },
  { key: "ai_key_skills_and_competencies", label: "AI Key Skills & Competencies" },
  { key: "ai_kpi", label: "AI KPI" },
  { key: "ai_job_overall_summary", label: "AI Overall Summary" },
];
const LONG_TEXT_FIELDS = new Set([
  "qualifications", "certifications", "job_summary",
  "key_responsibilities", "key_skills_and_competencies", "kpi",
  "ai_job_summary", "ai_qualifications", "ai_key_responsibilities",
  "ai_key_skills_and_competencies", "ai_kpi", "ai_job_overall_summary",
]);
const TRUNCATE_LENGTH = 200;

function normalise(value) {
  if (value === null || value === undefined || value === "" || value === "NULL") return null;
  return String(value).trim();
}

function initialFields(log) {
  return TRACKED_FIELDS.map(({ key, label }) => ({
    key,
    label,
    oldValue: null,
    newValue: normalise(log[key]),
  })).filter((d) => d.newValue !== null);
}

function diffLogs(prev, curr) {
  const diffs = [];
  for (const { key, label } of TRACKED_FIELDS) {
    const oldVal = normalise(prev?.[key]);
    const newVal = normalise(curr[key]);
    if (oldVal !== newVal) diffs.push({ key, label, oldValue: oldVal, newValue: newVal });
  }
  return diffs;
}

function buildLogEntries(logs) {
  // Logs come newest-first from the API - reverse so we diff oldest -> newest.
  const chronological = [...logs].reverse();
  const entries = chronological.map((log, index) => {
    const prev = index === 0 ? null : chronological[index - 1];
    const action = (log.action || "").toUpperCase();
    const diffs = action === "UPDATE" || action === "AI_UPDATE" ? diffLogs(prev, log) : initialFields(log);
    return {
      raw: log,
      changedAt: log.changed_at || log.update_date,
      action,
      diffs,
    };
  });
  // Reverse back so newest is at the top.
  return entries.reverse();
}

function truncate(value, fieldKey, expandedFields) {
  if (!value) return "-";
  const baseKey = fieldKey.replace(/_old$|_new$/, "");
  if (!LONG_TEXT_FIELDS.has(baseKey) || expandedFields.has(fieldKey)) return value;
  return value.length > TRUNCATE_LENGTH ? value.slice(0, TRUNCATE_LENGTH) + "…" : value;
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function ChangeHistoryDialog({ loading, entries, onClose }) {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [expandedFieldsById, setExpandedFieldsById] = useState({});
  // Ported from the real p-dialog's [maximizable]="true" - a working
  // fill-viewport toggle, not decorative.
  const [maximized, setMaximized] = useState(false);

  // Newest entry open by default, once entries actually load.
  useEffect(() => {
    if (entries.length > 0) setExpandedIds(new Set([entries[0].raw.id]));
  }, [entries]);

  const toggleEntry = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleField = (id, key) => {
    setExpandedFieldsById((prev) => {
      const set = new Set(prev[id] || []);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, [id]: set };
    });
  };

  return (
    <div className="jsp-modal-backdrop" onClick={onClose}>
      <div className={`jsp-modal jsp-modal-lg${maximized ? " jsp-modal-maximized" : ""}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Change History">
        <div className="jsp-modal-head">
          <h4>Change History</h4>
          <div className="jsp-modal-head-actions">
            <button type="button" className="jsp-modal-maximize" onClick={() => setMaximized((m) => !m)} title={maximized ? "Restore" : "Maximize"} aria-label={maximized ? "Restore" : "Maximize"}>
              {maximized ? "⤡" : "⤢"}
            </button>
            <button type="button" className="jsp-modal-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>
        <div className="jsp-modal-body">
          {loading && <div className="jsp-empty">Loading history…</div>}
          {!loading && entries.length === 0 && <div className="jsp-empty">No change history found.</div>}
          {!loading &&
            entries.map((entry) => {
              const isOpen = expandedIds.has(entry.raw.id);
              const fields = expandedFieldsById[entry.raw.id] || new Set();
              const isUpdate = entry.action === "UPDATE" || entry.action === "AI_UPDATE";
              return (
                <div
                  className={`jsp-history-entry${entry.action === "SEED" ? " jsp-entry-seed" : ""}${entry.action === "INSERT" ? " jsp-entry-insert" : ""}`}
                  key={entry.raw.id}
                >
                  <div className="jsp-history-entry-head" onClick={() => toggleEntry(entry.raw.id)}>
                    <span className={`jsp-history-badge jsp-badge-${entry.action.toLowerCase()}`}>{entry.action}</span>
                    <span className="jsp-history-date">{formatDate(entry.changedAt)}</span>
                    <span className="jsp-history-count">
                      {entry.diffs.length} field{entry.diffs.length !== 1 ? "s" : ""} {isUpdate ? "changed" : "set"}
                    </span>
                    <span className={`jsp-expand-icon${isOpen ? " open" : ""}`}>▾</span>
                  </div>
                  {isOpen && (
                    <div className="jsp-history-entry-body">
                      {entry.diffs.length === 0 ? (
                        <div className="jsp-empty-inline">No field changes detected.</div>
                      ) : isUpdate ? (
                        <table className="jsp-diff-table">
                          <thead>
                            <tr><th>Field</th><th>Before</th><th>After</th></tr>
                          </thead>
                          <tbody>
                            {entry.diffs.map((d) => (
                              <tr key={d.key}>
                                <td className="jsp-diff-label">{d.label}</td>
                                <td className="jsp-diff-old">
                                  {truncate(d.oldValue, `${d.key}_old`, fields)}
                                  {LONG_TEXT_FIELDS.has(d.key) && d.oldValue && d.oldValue.length > TRUNCATE_LENGTH && (
                                    <button type="button" className="jsp-expand-text" onClick={() => toggleField(entry.raw.id, `${d.key}_old`)}>
                                      {fields.has(`${d.key}_old`) ? "Show less" : "Show more"}
                                    </button>
                                  )}
                                </td>
                                <td className="jsp-diff-new">
                                  {truncate(d.newValue, `${d.key}_new`, fields)}
                                  {LONG_TEXT_FIELDS.has(d.key) && d.newValue && d.newValue.length > TRUNCATE_LENGTH && (
                                    <button type="button" className="jsp-expand-text" onClick={() => toggleField(entry.raw.id, `${d.key}_new`)}>
                                      {fields.has(`${d.key}_new`) ? "Show less" : "Show more"}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="jsp-insert-grid">
                          {entry.diffs.map((d) => (
                            <div className={`jsp-insert-field${LONG_TEXT_FIELDS.has(d.key) ? " jsp-insert-field-long" : ""}`} key={d.key}>
                              <span className="jsp-field-label">{d.label}</span>
                              <span className="jsp-field-value">
                                {truncate(d.newValue, d.key, fields)}
                                {LONG_TEXT_FIELDS.has(d.key) && d.newValue && d.newValue.length > TRUNCATE_LENGTH && (
                                  <button type="button" className="jsp-expand-text" onClick={() => toggleField(entry.raw.id, d.key)}>
                                    {fields.has(d.key) ? "Show less" : "Show more"}
                                  </button>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

/**
 * JobSpecPanel
 *
 * Collapsible port of job-specification.component.ts/.html - the comp
 * page's embedded single-role Job Specification (JD + Skill Set Matrix +
 * KPI + Change History). Mounted in two places, both collapsed by default:
 *  - a fixed panel at the bottom of the job-detail page (App.js)
 *  - inside each role's expand row on the role-list page (RoleListTable.jsx)
 *
 * jobTitleId: the role to show.
 * verticalStackId: drives the Skill Set Matrix inline-vs-modal split
 *   (Config.stackModel.verticalStackId in the real app - here, whichever
 *   Vertical Stack is currently selected, same source either way).
 */
export default function JobSpecPanel({ jobTitleId, verticalStackId }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jd, setJd] = useState(null);
  const [nonTechData, setNonTechData] = useState([]);
  const [skillMatrixInfo, setSkillMatrixInfo] = useState(null);

  const [showSkillModal, setShowSkillModal] = useState(false);
  const [skillModalMaximized, setSkillModalMaximized] = useState(false);

  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEntries, setHistoryEntries] = useState([]);

  const isModalVertical = SKILL_MATRIX_MODAL_VERTICALS.includes(verticalStackId);

  // Lazy-load on first expand (our own addition, since this panel is
  // collapsed by default and old's isn't) - but once loaded, fetch ALL
  // THREE unconditionally together, matching the real onfilterChange()'s
  // own Promise.all([GetJobDescription, GetSkillSetMatrixNonTech,
  // GetSkillSetMatrix]) exactly. The modal-vs-inline split for Skill Set
  // Matrix is a DISPLAY choice only (skills-matrix.component.html vs
  // skills-matrix-info.component.html) - confirmed via the real .ts, old
  // never delays that fetch until the modal button is clicked, so an extra
  // loading spinner on open (what this had before) is our own invented
  // deviation, not real behavior.
  useEffect(() => {
    if (!open || loaded || !jobTitleId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([getJobDescription(jobTitleId), getSkillSetMatrixNonTech(jobTitleId), getSkillSetMatrix(jobTitleId)])
      .then(([jdRes, nonTechRes, skillRes]) => {
        if (cancelled) return;
        setJd(jdRes || {});
        setNonTechData(nonTechRes || []);
        setSkillMatrixInfo(skillRes || null);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, loaded, jobTitleId]);

  // Reset everything when the role changes.
  useEffect(() => {
    setLoaded(false);
    setJd(null);
    setNonTechData([]);
    setSkillMatrixInfo(null);
    setShowSkillModal(false);
    setShowHistory(false);
    setHistoryEntries([]);
  }, [jobTitleId]);

  const openHistory = () => {
    if (!jd?.id) return;
    setShowHistory(true);
    setHistoryLoading(true);
    getJobDescriptionLogs(jd.id)
      .then((logs) => setHistoryEntries(buildLogEntries(logs || [])))
      .catch(() => setHistoryEntries([]))
      .finally(() => setHistoryLoading(false));
  };

  const keyResponsibilitiesData = jd ? parseJobDescriptionSections(jd.key_responsibilities) : [];
  const keySkillsData = jd ? parseJobDescriptionSections(jd.key_skills_and_competencies) : [];
  const kpiData = jd ? parseJobDescriptionSections(jd.kpi) : [];

  return (
    <section className="jsp-section">
      <button type="button" className="jsp-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className={`jsp-toggle-chevron${open ? " open" : ""}`}>▸</span>
        Job Specification
      </button>

      {open && (
        <div className="jsp-body">
          {!jobTitleId && <div className="jsp-empty">Select a job title to view the job specification.</div>}
          {jobTitleId && loading && <div className="jsp-empty">Loading…</div>}

          {jobTitleId && !loading && (
            <>
              <div className="jsp-card">
                <div className="jsp-card-head">
                  <h3 className="jsp-card-title">Job Specification</h3>
                  {jd?.id && (
                    <button type="button" className="jsp-history-btn" onClick={openHistory}>
                      Changed History
                    </button>
                  )}
                </div>
                <div className="jsp-jd-fields">
                  <div className="jsp-field-row"><span className="jsp-field-key">Job Title:</span><span className="jsp-field-val">{jd?.minerva_job_title}</span></div>
                  <div className="jsp-field-row"><span className="jsp-field-key">Alternate Job Title:</span><span className="jsp-field-val">{jd?.source_job_title_alternative}</span></div>
                  <div className="jsp-field-row"><span className="jsp-field-key">Reporting To:</span><span className="jsp-field-val">{jd?.reporting_to}</span></div>
                  <div className="jsp-field-row"><span className="jsp-field-key">Experience:</span><span className="jsp-field-val">{jd?.experience}</span></div>
                  <div className="jsp-field-row"><span className="jsp-field-key">Job Summary:</span><span className="jsp-field-val">{jd?.job_summary}</span></div>
                  <div className="jsp-field-row"><span className="jsp-field-key">Qualifications:</span><span className="jsp-field-val">{jd?.qualifications}</span></div>
                  <div className="jsp-field-block">
                    <div className="jsp-field-key">Key Responsibilities:</div>
                    <SectionList sections={keyResponsibilitiesData} />
                  </div>
                  <div className="jsp-field-block">
                    <div className="jsp-field-key">Key Skills And Competencies:</div>
                    <SectionList sections={keySkillsData} />
                  </div>
                </div>
              </div>

              <div className="jsp-card">
                {isModalVertical ? (
                  <>
                    <div className="jsp-subheading jsp-matrix-heading">Skill Set Matrix</div>
                    <button type="button" className="jsp-skillmatrix-btn" onClick={() => setShowSkillModal(true)}>
                      Skill Set Matrix for {jd?.minerva_job_title || "this role"}
                    </button>
                  </>
                ) : (
                  <InlineSkillMatrix info={skillMatrixInfo} nonTechData={nonTechData} roleName={jd?.minerva_job_title} />
                )}
              </div>

              <div className="jsp-card">
                <h3 className="jsp-card-title">Key Performance Indicators</h3>
                <SectionList sections={kpiData} />
              </div>
            </>
          )}
        </div>
      )}

      {showSkillModal && (
        <div className="jsp-modal-backdrop" onClick={() => setShowSkillModal(false)}>
          <div className={`jsp-modal jsp-modal-lg${skillModalMaximized ? " jsp-modal-maximized" : ""}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Skills Matrix">
            <div className="jsp-modal-head">
              <h4>Skills Matrix</h4>
              <div className="jsp-modal-head-actions">
                <button type="button" className="jsp-modal-maximize" onClick={() => setSkillModalMaximized((m) => !m)} title={skillModalMaximized ? "Restore" : "Maximize"} aria-label={skillModalMaximized ? "Restore" : "Maximize"}>
                  {skillModalMaximized ? "⤡" : "⤢"}
                </button>
                <button type="button" className="jsp-modal-close" onClick={() => setShowSkillModal(false)} aria-label="Close">×</button>
              </div>
            </div>
            <div className="jsp-modal-body">
              {/* Data is already loaded by the time this button is clickable
                  (see the eager-fetch effect above) - matches old's real
                  onfilterChange(), which fetches Skill Set Matrix unconditionally
                  up front, not lazily on button click. No loading state needed here. */}
              <ModalSkillMatrix info={skillMatrixInfo} roleName={jd?.minerva_job_title} />
            </div>
          </div>
        </div>
      )}

      {showHistory && (
        <ChangeHistoryDialog loading={historyLoading} entries={historyEntries} onClose={() => setShowHistory(false)} />
      )}
    </section>
  );
}
